<template>
  <aside class="conversation-sidebar">
    <div class="conversation-sidebar-search">
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <button
            type="button"
            class="conversation-sidebar-filter-btn"
            v-bind="attrs"
            v-on="on"
            :class="{ 'is-active': mentionsCount > 0 }"
            @click="$emit('open-mentions')"
          >
            <v-icon size="18" :color="mentionsCount > 0 ? '#0066FF' : '#555'">
              mdi-at
            </v-icon>
            <span v-if="mentionsCount" class="conversation-sidebar-filter-dot" />
          </button>
        </template>
        <span>{{ $t("conversation-my-mentions") }}</span>
      </v-tooltip>

      <div class="conversation-sidebar-filter-wrap" @click.stop>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <button
              type="button"
              class="conversation-sidebar-filter-btn"
              v-bind="attrs"
              v-on="on"
              :class="{ 'is-active': filterOpen || hasActiveFilter }"
              @click="toggleFilter"
            >
              <v-icon
                size="18"
                :color="filterOpen || hasActiveFilter ? '#0066FF' : '#555'"
              >
                mdi-filter-variant
              </v-icon>
              <span
                v-if="hasActiveFilter"
                class="conversation-sidebar-filter-dot"
              />
            </button>
          </template>
          <span>{{ $t("filter") }}</span>
        </v-tooltip>

        <div v-if="filterOpen" class="conversation-sidebar-filter-menu">
          <div class="conversation-sidebar-filter-menu-title">
            {{ $t("open") }} / {{ $t("closed") }}
          </div>
          <button
            v-for="opt in stateOptions"
            :key="'state-' + (opt.value || 'all')"
            type="button"
            class="conversation-sidebar-filter-option"
            :class="{ 'is-active': stateFilter === opt.value }"
            @click="setStateFilter(opt.value)"
          >
            <v-icon size="16" class="ml-1">{{ opt.icon }}</v-icon>
            <span>{{ $t(opt.labelKey) }}</span>
            <v-icon
              v-if="stateFilter === opt.value"
              size="16"
              color="#0066FF"
              class="conversation-sidebar-filter-check"
            >
              mdi-check
            </v-icon>
          </button>
        </div>
      </div>

      <div
        class="conversation-sidebar-search-shell"
        :class="{ 'is-focused': searchFocused, 'has-value': !!search }"
      >
        <v-icon
          size="16"
          class="conversation-sidebar-search-icon"
          :color="searchFocused ? '#0066FF' : '#8a8a8a'"
        >
          mdi-magnify
        </v-icon>
        <input
          v-model="search"
          type="text"
          class="conversation-sidebar-search-input"
          :placeholder="$t('search')"
          @focus="searchFocused = true"
          @blur="searchFocused = false"
          @input="onSearchInput"
        />
        <transition name="conversation-search-fade">
          <button
            v-if="search"
            type="button"
            class="conversation-sidebar-search-clear"
            :aria-label="$t('close')"
            @mousedown.prevent
            @click="clearSearch"
          >
            <v-icon size="16" color="#666">mdi-close</v-icon>
          </button>
        </transition>
      </div>
    </div>

    <div class="conversation-sidebar-tabs mt-2">
      <button
        v-for="tab in visibleTabs"
        :key="tab.key"
        type="button"
        class="conversation-sidebar-tab"
        :class="{ 'is-active': activeTab === tab.key }"
        @click="$emit('update:activeTab', tab.key)"
      >
        <v-icon v-if="!tabUnreadCount(tab.key)" x-small>{{
          tab.icon
        }}</v-icon>
        <span class="conversation-sidebar-tab-label">{{
          $t(tab.labelKey)
        }}</span>
        <span
          v-if="tabUnreadCount(tab.key)"
          class="conversation-sidebar-tab-badge"
        >
          {{ toPersianDigits(tabUnreadCount(tab.key)) }}
        </span>
      </button>
    </div>

    <div v-if="canCreate" class="conversation-sidebar-create">
      <button
        type="button"
        class="conversation-sidebar-create-btn"
        @click="$emit('create')"
      >
        <v-icon size="16" color="#0066FF">mdi-plus</v-icon>
        <span>
          {{
            activeTab === "group"
              ? $t("newGroup")
              : activeTab === "channel"
                ? $t("conversation-new-channel")
                : $t("conversation-new-personal")
          }}
        </span>
      </button>
    </div>

    <div
      class="conversation-sidebar-list-wrap"
      :class="{ 'has-fab': canCreateUnified }"
    >
      <div class="conversation-sidebar-list">
        <div v-if="loading" class="conversation-sidebar-loading">
          <v-progress-circular
            indeterminate
            color="#0066FF"
            size="28"
            width="2"
          />
        </div>
        <template v-else>
          <button
            v-for="item in filteredItems"
            :key="item.id"
            type="button"
            class="conversation-sidebar-item"
            :class="{ 'is-active': Number(selectedId) === Number(item.id) }"
            @click="$emit('select', item)"
          >
            <div class="conversation-sidebar-meta">
              <div class="conversation-sidebar-row">
                <span class="conversation-sidebar-title">
                  <template v-if="item.type === 'ticket'">
                    #{{ toPersianDigits(item.ticketId) }}
                    <template v-if="ticketDisplayTitle(item)">
                      {{ ticketDisplayTitle(item) }}
                    </template>
                  </template>
                  <template v-else>{{ item.title }}</template>
                </span>
                <span class="conversation-sidebar-time">{{ item.time }}</span>
              </div>
              <div class="conversation-sidebar-row">
                <span class="conversation-sidebar-preview">
                  <span
                    v-if="item.state === 'close'"
                    class="conversation-sidebar-closed-tag"
                  >
                    {{ $t("closed") }}
                  </span>
                  {{ item.preview }}
                </span>
                <span v-if="item.unread" class="conversation-sidebar-badge">
                  {{ toPersianDigits(item.unread) }}
                </span>
              </div>
            </div>
            <div
              class="conversation-sidebar-avatar"
              :style="{ background: item.avatarColor }"
            >
              <img
                v-if="item.type === 'personal' && item.avatarUrl"
                :src="item.avatarUrl"
                :alt="item.title || ''"
              />
              <span v-else-if="item.type === 'ticket'">#</span>
              <v-icon v-else-if="item.type === 'group'" dark small>
                mdi-account-group
              </v-icon>
              <v-icon v-else-if="item.type === 'channel'" dark small>
                mdi-bullhorn
              </v-icon>
              <span v-else>{{ titleInitial(item.title) }}</span>
              <span v-if="item.online" class="conversation-sidebar-online" />
            </div>
          </button>

          <div v-if="!filteredItems.length" class="conversation-sidebar-empty">
            {{ $t("no-data-found") }}
          </div>
        </template>
      </div>

      <button
        v-if="canCreateUnified"
        type="button"
        class="conversation-sidebar-fab"
        :aria-label="$t('conversation-new')"
        @click="$emit('create')"
      >
        <v-icon size="24" color="#fff">mdi-plus</v-icon>
      </button>
    </div>

    <slot name="new-chat" />
  </aside>
</template>

<script>
export default {
  name: "ConversationSidebar",
  props: {
    items: {
      type: Array,
      default() {
        return [];
      },
    },
    activeTab: {
      type: String,
      default: "ticket",
    },
    selectedId: {
      type: [String, Number],
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    stateFilter: {
      type: String,
      default: "",
    },
    mentionsCount: {
      type: Number,
      default: 0,
    },
    tabUnread: {
      type: Object,
      default() {
        return {
          all: 0,
          personal: 0,
          group: 0,
          channel: 0,
          ticket: 0,
        };
      },
    },
  },
  data() {
    return {
      search: "",
      searchFocused: false,
      searchTimer: null,
      filterOpen: false,
      tabs: [
        {
          key: "personal",
          labelKey: "conversation-personal",
          icon: "mdi-account-outline",
        },
        { key: "group", labelKey: "group", icon: "mdi-account-group-outline" },
        {
          key: "channel",
          labelKey: "conversation-channel",
          icon: "mdi-bullhorn-outline",
        },
        {
          key: "all",
          labelKey: "all",
          icon: "mdi-forum-outline",
        },
        { key: "ticket", labelKey: "tickets", icon: "mdi-ticket-outline" },
      ],
      stateOptions: [
        { value: "", labelKey: "all", icon: "mdi-forum-outline" },
        { value: "open", labelKey: "open", icon: "mdi-chat-processing-outline" },
        { value: "close", labelKey: "closed", icon: "mdi-chat-remove-outline" },
      ],
    };
  },
  computed: {
    isSupport() {
      try {
        const token = JSON.parse(localStorage.getItem("token") || "{}");
        return (
          Number(
            token &&
              token.init &&
              token.init.appBarsOpacity &&
              token.init.appBarsOpacity.abr
          ) === 83
        );
      } catch (e) {
        return false;
      }
    },
    visibleTabs() {
      const tabs = this.tabs || [];
      if (this.isSupport) {
        return tabs.filter(function (tab) {
          return tab.key !== "all";
        });
      }
      return tabs.filter(function (tab) {
        return tab.key !== "ticket";
      });
    },
    filteredItems() {
      var tab = this.activeTab;
      return (this.items || []).filter(function (item) {
        if (tab === "all") {
          return (
            item.type === "personal" ||
            item.type === "group" ||
            item.type === "channel"
          );
        }
        return item.type === tab;
      });
    },
    canCreate() {
      return (
        this.activeTab === "personal" ||
        this.activeTab === "group" ||
        this.activeTab === "channel"
      );
    },
    canCreateUnified() {
      return this.activeTab === "all";
    },
    hasActiveFilter() {
      return !!this.stateFilter;
    },
  },
  watch: {
    filterOpen(val) {
      if (val) {
        document.addEventListener("click", this.onDocumentClick, true);
      } else {
        document.removeEventListener("click", this.onDocumentClick, true);
      }
    },
  },
  beforeDestroy() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    document.removeEventListener("click", this.onDocumentClick, true);
  },
  methods: {
    onDocumentClick(event) {
      if (!this.filterOpen) {
        return;
      }
      var wrap =
        this.$el && this.$el.querySelector
          ? this.$el.querySelector(".conversation-sidebar-filter-wrap")
          : null;
      if (wrap && wrap.contains(event.target)) {
        return;
      }
      this.filterOpen = false;
    },
    toggleFilter() {
      this.filterOpen = !this.filterOpen;
    },
    onSearchInput() {
      var self = this;
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(function () {
        self.$emit("search", (self.search || "").trim());
      }, 400);
    },
    clearSearch() {
      this.search = "";
      this.$emit("search", "");
    },
    setStateFilter(value) {
      this.$emit("update:stateFilter", value);
      this.filterOpen = false;
    },
    titleInitial(title) {
      return title ? String(title).charAt(0) : "?";
    },
    toPersianDigits(value) {
      if (value == null) {
        return "";
      }
      return String(value).replace(/\d/g, function (d) {
        return "۰۱۲۳۴۵۶۷۸۹"[d];
      });
    },
    tabUnreadCount(key) {
      if (key === "all") {
        var meta = this.tabUnread || {};
        return Math.max(
          0,
          (Number(meta.personal) || 0) +
            (Number(meta.group) || 0) +
            (Number(meta.channel) || 0)
        );
      }
      var count = this.tabUnread && this.tabUnread[key];
      return Math.max(0, Number(count) || 0);
    },
    ticketDisplayTitle(item) {
      if (!item) {
        return "";
      }
      var title = String(item.title || "").trim();
      if (!title || item.ticketId == null) {
        return title;
      }
      var id = String(item.ticketId);
      var persian = this.toPersianDigits(id);
      if (
        title === id ||
        title === persian ||
        title === "#" + id ||
        title === "#" + persian
      ) {
        return "";
      }
      return title;
    },
  },
};
</script>

<style scoped>
.conversation-sidebar {
  width: 300px;
  min-width: 300px;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #ececec;
  background: #fafafa;
  direction: rtl;
  position: relative;
}
.conversation-sidebar-search {
  padding: 12px 12px 8px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 1px;
}
.conversation-sidebar-search-shell {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 8px;
  border-radius: 4px;
  background: #eef0f3;
  border: 1px solid transparent;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}
.conversation-sidebar-search-shell.is-focused {
  background: #fff;
  border-color: #0066ff;
  box-shadow: 0 0 0 2px rgba(0, 102, 255, 0.1);
  transform: none;
}
.conversation-sidebar-search-icon {
  flex: 0 0 auto;
  transition: color 0.2s ease;
}
.conversation-sidebar-search-shell.is-focused .conversation-sidebar-search-icon {
  transform: none;
}
.conversation-sidebar-search-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-size: 12px;
  font-family: inherit;
  color: #222;
  margin-right: 2px;
}
.conversation-sidebar-search-input::placeholder {
  color: #9aa0a6;
}
.conversation-sidebar-search-clear {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.06);
  cursor: pointer;
  padding: 0;
  transition: background 0.15s ease;
}
.conversation-sidebar-search-clear:hover {
  background: rgba(0, 0, 0, 0.1);
  transform: none;
}
.conversation-search-fade-enter-active,
.conversation-search-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.conversation-search-fade-enter,
.conversation-search-fade-leave-to {
  opacity: 0;
  transform: scale(0.85);
}
.conversation-sidebar-filter-wrap {
  position: relative;
  flex: 0 0 auto;
}
.conversation-sidebar-search > .v-tooltip,
.conversation-sidebar-filter-wrap > .v-tooltip {
  display: inline-flex;
  flex: 0 0 auto;
}
.conversation-sidebar-filter-btn {
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 4px;
  background: #eef0f3;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 0;
  margin-left: 3px;
}
.conversation-sidebar-filter-btn:hover,
.conversation-sidebar-filter-btn.is-active {
  background: #e8f1ff;
}
.conversation-sidebar-filter-dot {
  position: absolute;
  top: 6px;
  left: 6px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #0066ff;
  border: 1.5px solid #e8f1ff;
}
.conversation-sidebar-filter-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  left: auto;
  width: 200px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
  z-index: 20;
  overflow: hidden;
  padding: 6px;
}
.conversation-sidebar-filter-menu-title {
  padding: 6px 10px 8px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}
.conversation-sidebar-filter-option {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 10px;
  border: none;
  border-radius: 7px;
  background: transparent;
  cursor: pointer;
  text-align: right;
  font-size: 13px;
  font-family: inherit;
  color: #333;
}
.conversation-sidebar-filter-option:hover {
  background: #f3f6fb;
}
.conversation-sidebar-filter-option.is-active {
  background: #e8f1ff;
  color: #0066ff;
  font-weight: 600;
}
.conversation-sidebar-filter-check {
  margin-right: auto;
}
.conversation-sidebar-tabs {
  display: flex;
  gap: 4px;
  padding: 0 10px 10px;
  flex: 0 0 auto;
}
.conversation-sidebar-create {
  padding: 0 10px 10px;
  flex: 0 0 auto;
}
.conversation-sidebar-create-btn {
  width: 100%;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px dashed #b6d0ff;
  border-radius: 8px;
  background: #f5f9ff;
  color: #0066ff;
  font-size: 12px;
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
}
.conversation-sidebar-create-btn:hover {
  background: #e8f1ff;
}
.conversation-sidebar-tab {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-width: 0;
  height: 30px;
  padding: 0 4px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #666;
  font-size: 11px;
  cursor: pointer;
}
.conversation-sidebar-tab > .v-icon {
  flex: 0 0 auto;
  margin: 0 !important;
  font-size: 14px !important;
  width: 14px;
  height: 14px;
}
.conversation-sidebar-tab-label {
  flex: 0 1 auto;
  white-space: nowrap;
  line-height: 1.2;
}
.conversation-sidebar-tab.is-active {
  background: #e8f1ff;
  color: #0066ff;
  font-weight: 600;
}
.conversation-sidebar-tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 13px;
  height: 13px;
  padding: 0 3px;
  border-radius: 999px;
  background: #0066ff;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  flex: 0 0 auto;
}
.conversation-sidebar-tab.is-active .conversation-sidebar-tab-badge {
  background: #0052cc;
}
.conversation-sidebar-list-wrap {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.conversation-sidebar-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.18) transparent;
}
.conversation-sidebar-list-wrap.has-fab .conversation-sidebar-list {
  padding-bottom: 72px;
}
.conversation-sidebar-list::-webkit-scrollbar {
  width: 4px;
}
.conversation-sidebar-list::-webkit-scrollbar-track {
  background: transparent;
}
.conversation-sidebar-list::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.16);
  border-radius: 999px;
}
.conversation-sidebar-list::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.28);
}
.conversation-sidebar-fab {
  position: absolute;
  left: 14px;
  bottom: 14px;
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background: #0066ff;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(0, 102, 255, 0.35);
  z-index: 2;
}
.conversation-sidebar-fab:hover {
  background: #0052cc;
}
.conversation-sidebar-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: none;
  border-bottom: 1px solid #f0f0f0;
  background: transparent;
  text-align: right;
  cursor: pointer;
}
.conversation-sidebar-item:hover {
  background: #f3f6fb;
}
.conversation-sidebar-item.is-active {
  background: #eef4ff;
}
.conversation-sidebar-avatar {
  position: relative;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex: 0 0 auto;
  overflow: hidden;
}
.conversation-sidebar-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.conversation-sidebar-online {
  position: absolute;
  left: 1px;
  bottom: 1px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #22c55e;
  border: 2px solid #fafafa;
}
.conversation-sidebar-meta {
  flex: 1 1 auto;
  min-width: 0;
}
.conversation-sidebar-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.conversation-sidebar-title {
  font-size: 13px;
  font-weight: 600;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.conversation-sidebar-time {
  font-size: 11px;
  color: #9aa0a6;
  flex: 0 0 auto;
}
.conversation-sidebar-preview {
  font-size: 12px;
  color: #7a7a7a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
}
.conversation-sidebar-closed-tag {
  display: inline-block;
  margin-left: 4px;
  padding: 0 5px;
  border-radius: 4px;
  background: #fef3c7;
  color: #b45309;
  font-size: 10px;
}
.conversation-sidebar-badge {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #0066ff;
  color: #fff;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
  flex: 0 0 auto;
}
.conversation-sidebar-empty {
  padding: 24px 16px;
  text-align: center;
  color: #999;
  font-size: 13px;
}
.conversation-sidebar-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px 16px;
}
</style>
